import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Package, Image as ImageIcon, Tag, Truck, Sparkles, Loader2, Globe,
  ChevronLeft, X, Upload, Eye, ShieldCheck, AlertCircle, Mountain,
  Palette, Ruler, Layers, Search as SearchIcon, RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ImageSpec } from '@/components/shared/ImageSpec';
import {
  type ProductStatus, type ColorOption, type VariantSku,
} from '@/store/product-store';
import { vendorApi } from '@/services/api';
import { uploadImage } from '@/services/auth';

interface ApiCategory {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
}
import {
  Card, CardHeader, CardTitle, CardContent, Button, Input, Badge, Separator, cn, RichTextEditor,
} from '@nepal-ecom/ui';

// ─── Helpers ────────────────────────────────────────────────────────────────

function slugify(s: string) {
  return s.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

const SEO_LIMITS = { title: 60, description: 160 };

function seoScore(meta: { title: string; description: string; focus: string; slug: string; name: string }): {
  score: number; checks: Array<{ ok: boolean; label: string }>;
} {
  const checks = [
    { ok: meta.title.length >= 30 && meta.title.length <= SEO_LIMITS.title, label: `Meta title 30–${SEO_LIMITS.title} chars` },
    { ok: meta.description.length >= 70 && meta.description.length <= SEO_LIMITS.description, label: `Meta description 70–${SEO_LIMITS.description} chars` },
    { ok: !!meta.focus && (meta.title.toLowerCase().includes(meta.focus.toLowerCase()) || meta.name.toLowerCase().includes(meta.focus.toLowerCase())), label: 'Focus keyword in title or name' },
    { ok: !!meta.focus && meta.description.toLowerCase().includes(meta.focus.toLowerCase()), label: 'Focus keyword in description' },
    { ok: !!meta.slug && meta.slug.length <= 75 && !/[^a-z0-9-]/.test(meta.slug), label: 'Clean URL slug' },
  ];
  const ok = checks.filter((c) => c.ok).length;
  return { score: Math.round((ok / checks.length) * 100), checks };
}

// ─── Layout primitives ──────────────────────────────────────────────────────

function FormSection({
  icon: Icon, title, description, children, badge,
}: {
  icon: typeof Package;
  title: string;
  description?: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary-50 ring-1 ring-primary-100 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle>{title}</CardTitle>
              {badge}
            </div>
            {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Label({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-1.5">
      <label className="text-xs font-semibold text-slate-700">{children}</label>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(isEditing);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [apiCategories, setApiCategories] = useState<ApiCategory[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mainCategory, setMainCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState('');
  const [compareAt, setCompareAt] = useState('');
  const [stock, setStock] = useState('');
  const [madeInNepal, setMadeInNepal] = useState(false);
  const [codEligible, setCodEligible] = useState(true);
  const [tags, setTags] = useState<string[]>(['new']);
  const [tagDraft, setTagDraft] = useState('');
  const [status, setStatus] = useState<ProductStatus>('active');

  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [focusKeyword, setFocusKeyword] = useState('');

  const [weight, setWeight] = useState('');
  const [dimL, setDimL] = useState('');
  const [dimW, setDimW] = useState('');
  const [dimH, setDimH] = useState('');
  const [leadTime, setLeadTime] = useState('');

  const [colors, setColors] = useState<ColorOption[]>([]);
  const [colorName, setColorName] = useState('');
  const [colorHex, setColorHex] = useState('#E11D48');
  const [sizes, setSizes] = useState<string[]>([]);
  const [sizeDraft, setSizeDraft] = useState('');
  const [variants, setVariants] = useState<VariantSku[]>([]);

  // Existing product (for edit mode) — kept after fetch so we can resolve category once apiCategories arrives
  const [existingCategoryId, setExistingCategoryId] = useState<string | null>(null);

  // Fetch categories once
  useEffect(() => {
    vendorApi.getCategories()
      .then((res) => {
        const list = (res.data?.data ?? []) as ApiCategory[];
        setApiCategories(list);
      })
      .catch(() => toast.error('Could not load categories'));
  }, []);

  // Load existing product in edit mode
  useEffect(() => {
    if (!isEditing || !id) return;
    vendorApi.getProduct(id)
      .then((res) => {
        const p = res.data?.data;
        if (!p) {
          toast.error('Product not found');
          navigate('/products');
          return;
        }
        setName(p.name ?? '');
        setDescription(p.description ?? '');
        setSku(p.sku ?? '');
        setPrice(p.price ? String(p.price) : '');
        setCompareAt(p.compareAtPrice ? String(p.compareAtPrice) : '');
        setStock(p.stock != null ? String(p.stock) : '');
        setMadeInNepal(!!p.isMadeInNepal);
        setTags(p.tags ?? []);
        setStatus(p.status ?? 'active');
        setSlug(p.slug ?? '');
        setSlugTouched(true);
        setMetaTitle(p.metaTitle ?? '');
        setMetaDescription(p.metaDescription ?? '');
        setImagePreviews(p.images ?? (p.thumbnailUrl ? [p.thumbnailUrl] : []));
        setWeight(p.weight != null ? String(p.weight) : '');
        if (p.dimensions) {
          setDimL(p.dimensions.length != null ? String(p.dimensions.length) : '');
          setDimW(p.dimensions.width != null ? String(p.dimensions.width) : '');
          setDimH(p.dimensions.height != null ? String(p.dimensions.height) : '');
        }
        if (p.attributes?.leadTimeDays) setLeadTime(String(p.attributes.leadTimeDays));

        // Hydrate colors / sizes / variant matrix from the saved product so the
        // edit flow can mutate them. Without this the editor opened empty and a
        // save wiped existing variants. Color/size come from each variant's
        // `options` map; the `attributes.colors|sizes` strings are the fallback
        // for products that stored options but no per-combo rows.
        const apiVariants: Array<{
          sku?: string; price?: number | string; stock?: number;
          options?: { color?: string; size?: string } | null;
        }> = Array.isArray(p.variants) ? p.variants : [];
        const attrColors = typeof p.attributes?.colors === 'string'
          ? p.attributes.colors.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
        const attrSizes = typeof p.attributes?.sizes === 'string'
          ? p.attributes.sizes.split(',').map((s: string) => s.trim()).filter(Boolean) : [];

        const colorNames = new Set<string>(attrColors);
        const sizeNames = new Set<string>(attrSizes);
        for (const v of apiVariants) {
          if (v.options?.color) colorNames.add(v.options.color);
          if (v.options?.size) sizeNames.add(v.options.size);
        }

        // Variant rows persist names, not hex. Re-assign stable default swatches
        // so the picker has a color to show — names are what actually match.
        const SWATCHES = ['#E11D48', '#2563EB', '#059669', '#D97706', '#7C3AED', '#0891B2', '#DB2777', '#475569'];
        if (colorNames.size > 0) {
          setColors(Array.from(colorNames).map((nm, i) => ({ name: nm, hex: SWATCHES[i % SWATCHES.length] })));
        }
        if (sizeNames.size > 0) setSizes(Array.from(sizeNames));

        if (apiVariants.length > 0) {
          setVariants(apiVariants.map((v) => {
            const color = v.options?.color;
            const size = v.options?.size;
            // Same composite key scheme as expectedVariantIds so the matrix
            // lines up and "Generate matrix" preserves these edits.
            const vid = `${color ? slugify(color) : '-'}|${size ? slugify(size) : '-'}`;
            return {
              id: vid,
              color,
              size,
              sku: v.sku ?? '',
              price: Number(v.price) || 0,
              stock: Number(v.stock) || 0,
            } as VariantSku;
          }));
        }

        if (p.categoryId) setExistingCategoryId(p.categoryId);
      })
      .catch((e) => {
        toast.error('Failed to load product', { description: (e as Error).message });
        navigate('/products');
      })
      .finally(() => setLoadingExisting(false));
  }, [id, isEditing, navigate]);

  // Build dynamic main/sub options from API categories. "Mains" are real
  // top-level categories PLUS orphans whose parent isn't in the active set
  // (e.g. a deactivated intermediate). Surfacing orphans keeps deep categories
  // selectable instead of vanishing when an ancestor is hidden.
  const mainOptions = useMemo(() => {
    const present = new Set(apiCategories.map((c) => c.id));
    return apiCategories
      .filter((c) => !c.parentId || !present.has(c.parentId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [apiCategories]);
  // All descendants of the chosen main category, depth-first with a depth marker
  // for indentation — so deep subcategories (e.g. Computer Accessories >
  // Keyboards under Electronics) are selectable, not just direct children.
  const availableSubs = useMemo(() => {
    if (!mainCategory) return [] as Array<ApiCategory & { depth: number }>;
    const out: Array<ApiCategory & { depth: number }> = [];
    const walk = (parentId: string, depth: number) => {
      const kids = apiCategories
        .filter((c) => c.parentId === parentId)
        .sort((a, b) => a.name.localeCompare(b.name));
      for (const kid of kids) {
        out.push({ ...kid, depth });
        walk(kid.id, depth + 1);
      }
    };
    walk(mainCategory, 0);
    return out;
  }, [apiCategories, mainCategory]);

  // Walk up to the top-level ancestor of a category.
  const topAncestorId = (catId: string): string => {
    let cur = apiCategories.find((c) => c.id === catId);
    while (cur?.parentId) {
      const parent = apiCategories.find((c) => c.id === cur!.parentId);
      if (!parent) break;
      cur = parent;
    }
    return cur?.id ?? catId;
  };

  // Once apiCategories load + we have an existing product's categoryId, prefill main/sub state
  useEffect(() => {
    if (apiCategories.length === 0 || !existingCategoryId) return;
    const cat = apiCategories.find((c) => c.id === existingCategoryId);
    if (!cat) return;
    if (cat.parentId) {
      setMainCategory(topAncestorId(cat.id));
      setSubCategory(cat.id);
    } else {
      setMainCategory(cat.id);
      setSubCategory('');
    }
  }, [apiCategories, existingCategoryId]);

  // When main category changes, clear sub if it's no longer a descendant of main
  useEffect(() => {
    if (subCategory && !availableSubs.some((c) => c.id === subCategory)) {
      setSubCategory('');
    }
  }, [mainCategory, subCategory, availableSubs]);

  // Names for the breadcrumb preview
  const currentMain = mainOptions.find((c) => c.id === mainCategory) ?? null;
  const currentSub = availableSubs.find((c) => c.id === subCategory) ?? null;

  // Resolve to a categoryId: sub overrides main
  const resolveCategoryId = (mainId: string, subId: string): string | null => subId || mainId || null;

  // Auto-slug from name unless manually edited
  useEffect(() => {
    if (!slugTouched && !isEditing) setSlug(slugify(name));
  }, [name, slugTouched, isEditing]);

  const discount = price && compareAt && Number(compareAt) > Number(price)
    ? Math.round(((Number(compareAt) - Number(price)) / Number(compareAt)) * 100)
    : 0;

  // ─── Tags ────────────────────────────────────────────────────────────────
  const addTag = () => {
    const t = tagDraft.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags((v) => [...v, t]);
    setTagDraft('');
  };
  const removeTag = (t: string) => setTags((v) => v.filter((x) => x !== t));

  // ─── Colors ──────────────────────────────────────────────────────────────
  const addColor = () => {
    const name = colorName.trim();
    if (!name) return;
    if (colors.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.error('Color already added');
      return;
    }
    setColors((arr) => [...arr, { name, hex: colorHex }]);
    setColorName('');
  };
  const removeColor = (name: string) => {
    setColors((arr) => arr.filter((c) => c.name !== name));
    setVariants((arr) => arr.filter((v) => v.color !== name));
  };

  // ─── Sizes ───────────────────────────────────────────────────────────────
  const addSize = () => {
    const s = sizeDraft.trim();
    if (!s) return;
    if (sizes.includes(s)) {
      toast.error('Size already added');
      return;
    }
    setSizes((arr) => [...arr, s]);
    setSizeDraft('');
  };
  const removeSize = (s: string) => {
    setSizes((arr) => arr.filter((x) => x !== s));
    setVariants((arr) => arr.filter((v) => v.size !== s));
  };

  // ─── Variant matrix generation ───────────────────────────────────────────
  const expectedVariantIds = useMemo(() => {
    const colorKeys = colors.length ? colors.map((c) => c.name) : [undefined];
    const sizeKeys = sizes.length ? sizes : [undefined];
    const out: Array<{ id: string; color?: string; size?: string }> = [];
    for (const c of colorKeys) {
      for (const s of sizeKeys) {
        if (c === undefined && s === undefined) continue;
        const id = `${c ? slugify(c) : '-'}|${s ? slugify(s) : '-'}`;
        out.push({ id, color: c, size: s });
      }
    }
    return out;
  }, [colors, sizes]);

  const regenerateMatrix = () => {
    if (expectedVariantIds.length === 0) {
      setVariants([]);
      return;
    }
    const basePrice = Number(price) || 0;
    const next: VariantSku[] = expectedVariantIds.map(({ id, color, size }) => {
      const existing = variants.find((v) => v.id === id);
      if (existing) return existing;
      const suffix = [color, size].filter(Boolean).map((p) => slugify(String(p)).toUpperCase().slice(0, 4)).join('-');
      return {
        id,
        color,
        size,
        sku: `${(sku || slug || 'SKU').toUpperCase().slice(0, 8)}-${suffix}`,
        price: basePrice,
        stock: 0,
      };
    });
    setVariants(next);
    toast.success(`Generated ${next.length} variant${next.length === 1 ? '' : 's'}`);
  };

  const updateVariant = (id: string, patch: Partial<VariantSku>) => {
    setVariants((arr) => arr.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  };

  const variantStockTotal = useMemo(() => variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0), [variants]);
  const hasVariants = variants.length > 0;

  // ─── SEO score ───────────────────────────────────────────────────────────
  const seo = useMemo(() => seoScore({
    title: metaTitle, description: metaDescription, focus: focusKeyword, slug, name,
  }), [metaTitle, metaDescription, focusKeyword, slug, name]);

  // ─── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent | React.MouseEvent, asDraft = false) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!price || Number(price) <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    if (!mainCategory) {
      toast.error('Pick a main category');
      return;
    }
    if (availableSubs.length > 0 && !subCategory) {
      toast.error('Pick a sub-category');
      return;
    }
    const categoryId = resolveCategoryId(mainCategory, subCategory);
    if (!categoryId) {
      toast.error('Could not resolve category. Categories may still be loading.');
      return;
    }

    setSubmitting(true);

    const apiPayload = {
      categoryId,
      name: name.trim(),
      description: description.trim() || undefined,
      sku: sku.trim() || undefined,
      price: Number(price),
      compareAtPrice: compareAt ? Number(compareAt) : undefined,
      stock: hasVariants ? variantStockTotal : (stock ? Number(stock) : 0),
      isMadeInNepal: madeInNepal,
      images: imagePreviews,
      thumbnailUrl: imagePreviews[0],
      tags,
      metaTitle: metaTitle.trim() || undefined,
      metaDescription: metaDescription.trim() || undefined,
      weight: weight ? Number(weight) : undefined,
      dimensions:
        dimL || dimW || dimH
          ? {
              length: Number(dimL) || 0,
              width: Number(dimW) || 0,
              height: Number(dimH) || 0,
              unit: 'cm' as const,
            }
          : undefined,
      attributes: {
        ...(codEligible ? { codAvailable: 'true' } : {}),
        ...(colors.length ? { colors: colors.map((c) => c.name).join(',') } : {}),
        ...(sizes.length ? { sizes: sizes.join(',') } : {}),
        ...(leadTime ? { leadTimeDays: leadTime } : {}),
      },
      ...(hasVariants
        ? {
            variants: variants.map((v) => ({
              name: [v.color, v.size].filter(Boolean).join(' / ') || v.sku,
              sku: v.sku,
              options: {
                ...(v.color ? { color: v.color } : {}),
                ...(v.size ? { size: v.size } : {}),
              },
              price: Number(v.price) || 0,
              stock: Number(v.stock) || 0,
            })),
          }
        : {}),
    } as Record<string, unknown>;

    try {
      if (isEditing && id) {
        await vendorApi.updateProduct(id, {
          ...apiPayload,
          status: asDraft ? 'draft' : status,
        });
        toast.success(asDraft ? 'Saved as draft' : 'Product updated', { description: name });
      } else {
        const res = await vendorApi.createProduct(apiPayload);
        const created = res.data?.data;
        if (created?.id && (asDraft ? 'draft' : status) !== 'draft') {
          await vendorApi.publishProduct(created.id).catch(() => {});
        } else if (created?.id && asDraft) {
          await vendorApi.updateProduct(created.id, { status: 'draft' }).catch(() => {});
        }
        toast.success(asDraft ? 'Draft saved' : 'Product published', { description: name });
      }
      navigate('/products');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        ?? (e as Error).message;
      toast.error('Save failed', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/products" className="hover:text-slate-900 inline-flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Products
        </Link>
        <span className="text-slate-300">/</span>
        <span className="text-slate-900 font-medium">{isEditing ? 'Edit product' : 'New product'}</span>
      </div>

      <PageHeader
        title={isEditing ? 'Edit product' : 'Add a product'}
        description="Add a new product to your store. Anything customers see — name, photos, price, stock, variants, SEO — lives here."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={(e) => handleSubmit(e, true)} disabled={submitting}>
              Save draft
            </Button>
            <Button onClick={(e) => handleSubmit(e, false)} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Publish'}
            </Button>
          </div>
        }
      />

      <form onSubmit={(e) => handleSubmit(e, false)} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* ── Product details ─────────────────────────────────────────── */}
          <FormSection
            icon={Package}
            title="Product details"
            description="Clear titles and descriptions help customers find your product."
          >
            <div>
              <Label hint={`${name.length}/120`}>Product name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="e.g. Hand-beaten Brass Singing Bowl, 6 inch"
              />
            </div>

            <div>
              <Label>Description</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Describe what makes this product special. Materials, size, who it's for…"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label hint={apiCategories.length === 0 ? 'Loading…' : undefined}>Main category *</Label>
                <select
                  value={mainCategory}
                  onChange={(e) => setMainCategory(e.target.value)}
                  disabled={apiCategories.length === 0}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-4 focus:ring-primary-500/15 focus:border-primary-400"
                >
                  <option value="">Select main category</option>
                  {mainOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label hint={!mainCategory ? 'Pick a main first' : undefined}>Sub-category {availableSubs.length > 0 && '*'}</Label>
                <select
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                  disabled={!mainCategory}
                  className={cn(
                    'h-10 w-full rounded-lg border bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-4 focus:ring-primary-500/15 focus:border-primary-400',
                    !mainCategory ? 'border-slate-100 text-slate-400 bg-slate-50 cursor-not-allowed' : 'border-slate-200',
                  )}
                >
                  <option value="">{mainCategory ? 'Select sub-category' : '—'}</option>
                  {availableSubs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${'  '.repeat(s.depth)}${s.depth > 0 ? '└ ' : ''}${s.name}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {currentMain && currentSub && (
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <span>Breadcrumb:</span>
                <Badge variant="secondary" className="text-[10px]">{currentMain.name}</Badge>
                <span className="text-slate-300">›</span>
                <Badge variant="secondary" className="text-[10px]">{currentSub.name}</Badge>
              </div>
            )}

            <div>
              <Label>Base SKU</Label>
              <Input
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="Optional — e.g. SB-BAGMATI-01"
              />
            </div>
          </FormSection>

          {/* ── Photos ───────────────────────────────────────────────────── */}
          <FormSection
            icon={ImageIcon}
            title="Photos"
            description="Up to 8 photos. First photo is the cover and is also used for social sharing."
          >
            <ImageSpec spec="productPhoto" variant="card" />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {imagePreviews.map((src, i) => (
                <div key={src} className="relative aspect-square rounded-xl overflow-hidden ring-1 ring-slate-200 group">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  {i === 0 && (
                    <Badge className="absolute top-2 left-2 bg-slate-900 text-white border-0">Cover</Badge>
                  )}
                  <button
                    type="button"
                    onClick={() => setImagePreviews((v) => v.filter((x) => x !== src))}
                    className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/90 hover:bg-white text-slate-700 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {imagePreviews.length < 8 && (
                <label
                  className={cn(
                    'aspect-square rounded-xl border-2 border-dashed border-slate-200 hover:border-primary-400 hover:bg-primary-50/30 transition-colors flex flex-col items-center justify-center gap-1.5 text-slate-500 hover:text-primary-700 cursor-pointer',
                    uploadingPhoto && 'opacity-60 cursor-wait',
                  )}
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    disabled={uploadingPhoto}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingPhoto(true);
                      try {
                        const url = await uploadImage(file);
                        setImagePreviews((v) => [...v, url]);
                      } catch (err) {
                        toast.error('Upload failed', { description: (err as Error).message });
                      } finally {
                        setUploadingPhoto(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {uploadingPhoto ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                  <span className="text-xs font-semibold">{uploadingPhoto ? 'Uploading…' : 'Add photo'}</span>
                </label>
              )}
            </div>
          </FormSection>

          {/* ── Pricing & inventory ──────────────────────────────────────── */}
          <FormSection icon={Tag} title="Pricing & inventory">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Price (रु.) *</Label>
                <Input type="number" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <Label hint="Show as crossed-out">Compare at</Label>
                <Input type="number" inputMode="decimal" value={compareAt} onChange={(e) => setCompareAt(e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <Label hint={hasVariants ? 'from variants' : undefined}>Stock *</Label>
                <Input
                  type="number"
                  value={hasVariants ? String(variantStockTotal) : stock}
                  disabled={hasVariants}
                  onChange={(e) => setStock(e.target.value)}
                  placeholder="0"
                  className={cn(hasVariants && 'bg-slate-50 text-slate-500')}
                />
              </div>
            </div>

            {discount > 0 && (
              <div className="flex items-center gap-2 rounded-lg bg-primary-50 ring-1 ring-primary-100 px-3 py-2 text-xs">
                <Sparkles className="h-3.5 w-3.5 text-primary-600" />
                <span className="font-semibold text-primary-700">{discount}% off</span>
                <span className="text-slate-600">will show as a discount badge to customers.</span>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 hover:border-slate-300 p-3 cursor-pointer">
                <input type="checkbox" checked={madeInNepal} onChange={(e) => setMadeInNepal(e.target.checked)} className="mt-1 accent-primary-600" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mountain className="h-4 w-4 text-primary-600" />
                    <p className="text-sm font-semibold text-slate-900">Made in Nepal</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Adds the green “🏔 Nepal” badge.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 hover:border-slate-300 p-3 cursor-pointer">
                <input type="checkbox" checked={codEligible} onChange={(e) => setCodEligible(e.target.checked)} className="mt-1 accent-primary-600" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-emerald-600" />
                    <p className="text-sm font-semibold text-slate-900">Cash on delivery</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Customers can pay when item arrives.</p>
                </div>
              </label>
            </div>
          </FormSection>

          {/* ── Variants ─────────────────────────────────────────────────── */}
          <FormSection
            icon={Layers}
            title="Variants — colors & sizes"
            description="Add colors and sizes if your product comes in multiple options. Each combination becomes its own SKU."
            badge={hasVariants ? <Badge variant="secondary">{variants.length} variant{variants.length === 1 ? '' : 's'}</Badge> : undefined}
          >
            {/* Colors */}
            <div>
              <Label hint="Click a chip to remove">
                <span className="inline-flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Colors</span>
              </Label>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {colors.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => removeColor(c.name)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1 pr-2.5 py-1 text-xs font-medium hover:border-red-300 hover:bg-red-50 group"
                  >
                    <span className="h-5 w-5 rounded-full ring-1 ring-slate-200" style={{ background: c.hex }} />
                    {c.name}
                    <X className="h-3 w-3 text-slate-400 group-hover:text-red-600" />
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={colorName}
                  onChange={(e) => setColorName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addColor(); } }}
                  placeholder="Color name (e.g. Royal Blue)"
                  className="h-9 sm:max-w-[220px]"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={colorHex}
                    onChange={(e) => setColorHex(e.target.value)}
                    className="h-9 w-12 rounded-lg border border-slate-200 cursor-pointer bg-white"
                    aria-label="Pick color"
                  />
                  <Input
                    value={colorHex}
                    onChange={(e) => setColorHex(e.target.value)}
                    className="h-9 max-w-[110px] font-mono text-xs"
                  />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addColor}>Add color</Button>
              </div>
            </div>

            {/* Sizes */}
            <div>
              <Label>
                <span className="inline-flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5" /> Sizes</span>
              </Label>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => removeSize(s)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold hover:border-red-300 hover:bg-red-50 group"
                  >
                    {s}
                    <X className="h-3 w-3 text-slate-400 group-hover:text-red-600" />
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={sizeDraft}
                  onChange={(e) => setSizeDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSize(); } }}
                  placeholder='e.g. S, M, L or 6"'
                  className="h-9 max-w-[200px]"
                />
                <Button type="button" variant="outline" size="sm" onClick={addSize}>Add size</Button>
                {(colors.length > 0 || sizes.length > 0) && (
                  <Button type="button" variant="outline" size="sm" onClick={regenerateMatrix}>
                    <RefreshCw className="h-3.5 w-3.5" /> Generate matrix
                  </Button>
                )}
              </div>
            </div>

            {/* Variant matrix table */}
            {hasVariants && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {colors.length > 0 && <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Color</th>}
                      {sizes.length > 0 && <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Size</th>}
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">SKU</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Price (रु.)</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Stock</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {variants.map((v) => {
                      const colorObj = colors.find((c) => c.name === v.color);
                      return (
                        <tr key={v.id}>
                          {colors.length > 0 && (
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700">
                                {colorObj && <span className="h-3.5 w-3.5 rounded-full ring-1 ring-slate-200" style={{ background: colorObj.hex }} />}
                                {v.color ?? '—'}
                              </span>
                            </td>
                          )}
                          {sizes.length > 0 && <td className="px-3 py-2 text-xs font-semibold text-slate-700">{v.size ?? '—'}</td>}
                          <td className="px-3 py-2">
                            <Input
                              value={v.sku}
                              onChange={(e) => updateVariant(v.id, { sku: e.target.value })}
                              className="h-7 text-xs font-mono"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={v.price}
                              onChange={(e) => updateVariant(v.id, { price: Number(e.target.value) })}
                              className="h-7 max-w-[100px] text-xs tabular-nums"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={v.stock}
                              onChange={(e) => updateVariant(v.id, { stock: Number(e.target.value) })}
                              className="h-7 max-w-[80px] text-xs tabular-nums"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!hasVariants && (colors.length > 0 || sizes.length > 0) && (
              <p className="text-xs text-slate-500 italic">
                You've added options but haven't generated the variant matrix yet. Click <strong>Generate matrix</strong> above.
              </p>
            )}
          </FormSection>

          {/* ── Shipping ─────────────────────────────────────────────────── */}
          <FormSection icon={Truck} title="Shipping" description="We use this to calculate delivery fees and timelines.">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Weight (kg)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.50"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </div>
              <div>
                <Label>L × W × H (cm)</Label>
                <div className="flex gap-2">
                  <Input placeholder="L" value={dimL} onChange={(e) => setDimL(e.target.value)} />
                  <Input placeholder="W" value={dimW} onChange={(e) => setDimW(e.target.value)} />
                  <Input placeholder="H" value={dimH} onChange={(e) => setDimH(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Lead time (days)</Label>
                <Input
                  type="number"
                  placeholder="2"
                  value={leadTime}
                  onChange={(e) => setLeadTime(e.target.value)}
                />
              </div>
            </div>
          </FormSection>

          {/* ── SEO ──────────────────────────────────────────────────────── */}
          <FormSection
            icon={Globe}
            title="SEO & search visibility"
            description="How this product appears in Google and on social shares."
            badge={
              <Badge variant={seo.score >= 80 ? 'success' : seo.score >= 50 ? 'warning' : 'destructive'}>
                SEO {seo.score}/100
              </Badge>
            }
          >
            <div>
              <Label hint={`${slug.length}/80`}>URL slug</Label>
              <div className="flex items-center rounded-lg border border-slate-200 bg-white shadow-xs overflow-hidden focus-within:ring-4 focus-within:ring-primary-500/15 focus-within:border-primary-400">
                <span className="px-3 text-xs text-slate-500 bg-slate-50 border-r border-slate-200 h-10 inline-flex items-center">shishyastra.com/product/</span>
                <input
                  value={slug}
                  onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
                  maxLength={80}
                  placeholder="hand-beaten-singing-bowl"
                  className="flex-1 px-3 text-sm font-mono outline-none bg-transparent h-10"
                />
              </div>
            </div>

            <div>
              <Label hint={`${metaTitle.length}/${SEO_LIMITS.title}`}>Meta title</Label>
              <Input
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                maxLength={SEO_LIMITS.title + 20}
                placeholder={name || 'How this title appears in Google results'}
              />
            </div>

            <div>
              <Label hint={`${metaDescription.length}/${SEO_LIMITS.description}`}>Meta description</Label>
              <textarea
                rows={3}
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                maxLength={SEO_LIMITS.description + 40}
                placeholder="A 1–2 sentence summary that appears under the title in Google results."
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-4 focus:ring-primary-500/15 focus:border-primary-400"
              />
            </div>

            <div>
              <Label>Focus keyword</Label>
              <Input
                value={focusKeyword}
                onChange={(e) => setFocusKeyword(e.target.value)}
                placeholder="e.g. singing bowl Nepal"
              />
            </div>

            <div>
              <Label hint="Auto-uses your cover photo">Social share image</Label>
              <ImageSpec spec="ogImage" variant="card" />
            </div>

            {/* Google search preview */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 inline-flex items-center gap-1">
                <SearchIcon className="h-3 w-3" /> Google preview
              </p>
              <div className="bg-white rounded-lg p-4 ring-1 ring-slate-200">
                <p className="text-xs text-slate-600 truncate">shishyastra.com › product › {slug || 'your-slug'}</p>
                <p className="text-lg text-cobalt-700 font-medium truncate mt-0.5 hover:underline cursor-default">
                  {metaTitle || name || 'Your meta title will appear here'}
                </p>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                  {metaDescription || description || 'Your meta description preview shows here. Aim for 70–160 characters that include the focus keyword.'}
                </p>
              </div>
            </div>

            {/* SEO checks */}
            <ul className="space-y-1.5">
              {seo.checks.map((c) => (
                <li key={c.label} className="flex items-center gap-2 text-xs">
                  <span className={cn('inline-flex h-4 w-4 rounded-full items-center justify-center text-[10px] font-bold', c.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400')}>
                    {c.ok ? '✓' : '·'}
                  </span>
                  <span className={cn(c.ok ? 'text-slate-700' : 'text-slate-500')}>{c.label}</span>
                </li>
              ))}
            </ul>
          </FormSection>

          {/* ── Tags ─────────────────────────────────────────────────────── */}
          <FormSection icon={Tag} title="Tags" description="Help shoppers discover your product in search.">
            <div className="flex flex-wrap gap-2 items-center">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1.5 py-1 text-xs font-semibold">
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="h-4 w-4 rounded-full hover:bg-slate-200 flex items-center justify-center" aria-label={`Remove ${t}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <div className="flex gap-2">
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Add tag…"
                  className="h-8 w-32"
                />
                <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
              </div>
            </div>
          </FormSection>
        </div>

        {/* ── Sidebar ────────────────────────────────────────────────────── */}
        <aside className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Status</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ProductStatus)}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-4 focus:ring-primary-500/15 focus:border-primary-400"
              >
                <option value="active">Active (visible to customers)</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              <Separator />
              <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 flex items-start gap-2 text-xs">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-900 leading-snug">
                  New products from unverified stores are hidden until your store is approved.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Eye className="h-4 w-4 text-primary-600" /> Live preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl border border-slate-200 bg-white shadow-product overflow-hidden">
                <div className="aspect-square bg-slate-50 relative">
                  {imagePreviews[0] && <img src={imagePreviews[0]} alt="" className="w-full h-full object-cover" />}
                  {madeInNepal && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-primary-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-sm">🏔 Nepal</span>
                  )}
                  {discount > 0 && (
                    <span className="absolute top-2 right-2 bg-secondary-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-sm tabular-nums">-{discount}%</span>
                  )}
                </div>
                <div className="p-3.5">
                  <p className="text-xs text-slate-500">Your store</p>
                  <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-snug mt-0.5">
                    {name || 'Your product name will appear here'}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1.5">
                    <span className="font-baloo font-bold text-slate-900 tabular-nums">रु. {price || '0'}</span>
                    {compareAt && Number(compareAt) > Number(price) && (
                      <span className="font-baloo text-xs text-slate-400 line-through tabular-nums">रु. {compareAt}</span>
                    )}
                  </div>
                  {colors.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-2">
                      {colors.slice(0, 5).map((c) => (
                        <span key={c.name} className="h-3.5 w-3.5 rounded-full ring-1 ring-slate-200" style={{ background: c.hex }} title={c.name} />
                      ))}
                      {colors.length > 5 && <span className="text-[10px] text-slate-500">+{colors.length - 5}</span>}
                    </div>
                  )}
                  {sizes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {sizes.map((s) => (
                        <span key={s} className="text-[10px] font-semibold text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Best practices</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-xs text-slate-600">
                <li className="flex gap-2"><span className="text-emerald-500">✓</span> Use 4+ photos from different angles</li>
                <li className="flex gap-2"><span className="text-emerald-500">✓</span> Pick the most specific sub-category</li>
                <li className="flex gap-2"><span className="text-emerald-500">✓</span> Add colors/sizes if your product has them</li>
                <li className="flex gap-2"><span className="text-emerald-500">✓</span> Fill in the SEO panel for Google visibility</li>
                <li className="flex gap-2"><span className="text-emerald-500">✓</span> Keep stock numbers updated per variant</li>
              </ul>
            </CardContent>
          </Card>
        </aside>
      </form>
    </div>
  );
}
